import type { BriefItem } from '../../logic/brief';
import { btn, linkBtn, row } from '../../ui/components';
import { h, span } from '../../ui/dom';
import { PERSONA_LABELS } from '../../ui/labels';
import { draftSheet } from './draftSheet';

/** One line of the daily brief (D89): what it is, why it is there today, and the ways to act on it
 *  with one thumb — call, walk to it, log it, or read the draft that fits. */

const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

/** Where the item opens: its lead page, or the New businesses card when it is not a lead yet. */
const hrefFor = (item: BriefItem): string | undefined => {
  if (item.lead_id) return `#/leads/${encodeURIComponent(item.lead_id)}`;
  if (item.signal_id) return `#/signals?id=${encodeURIComponent(item.signal_id)}`;
  return undefined;
};

const TONES: Record<BriefItem['kind'], 'danger' | 'warn' | 'accent'> = {
  follow_up: 'warn',
  call: 'accent',
  stop: 'accent',
};

export const briefRow = (item: BriefItem, index?: number): HTMLElement => {
  const actions = h(
    'div',
    { class: 'brief__actions' },
    item.phone
      ? h(
          'a',
          { class: 'btn btn--subtle btn--sm', href: telHref(item.phone), 'aria-label': `Call ${item.name}` },
          'Call',
        )
      : null,
    item.draft
      ? btn('Draft', {
          variant: 'subtle',
          size: 'sm',
          ariaLabel: `Draft for ${item.name}`,
          onClick: () => draftSheet(item.name, item.draft as NonNullable<BriefItem['draft']>, item.alternates),
        })
      : null,
    item.lead_id
      ? linkBtn('Log', `#/log?lead=${encodeURIComponent(item.lead_id)}`, { variant: 'subtle', size: 'sm' })
      : null,
  );

  return h(
    'div',
    { class: `brief__item${item.done ? ' brief__item--done' : ''}` },
    row({
      title: [
        index === undefined ? null : span('brief__num mono', String(index + 1)),
        span('row__name', item.name),
      ],
      meta: [
        item.done ? span('brief__done mono', 'Worked today') : null,
        item.done ? ' · ' : '',
        span('brief__reason', item.reason),
        item.where ? ` · ${item.where}` : '',
        ' · ',
        span('brief__persona mono', PERSONA_LABELS[item.persona]),
      ],
      href: hrefFor(item),
      tone: TONES[item.kind],
    }),
    actions,
    item.draft
      ? null
      : h(
          'p',
          { class: 'brief__nodraft' },
          `No ${PERSONA_LABELS[item.persona].toLowerCase()} message for this one in the library yet — open the door on what you know.`,
        ),
  );
};
